import {
  inject,
  Injectable,
  Signal,
  signal,
} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {ApiDeputyService} from '../../../infrastructure/api/common-feature/api-deputy/api-deputy.service';
import {ErrorService} from '../../../infrastructure/page/error/error/error.service';
import {UserAclService} from '../../../infrastructure/auth/acl/user-acl/user-acl.service';
import {IDeputy} from '../../../../models/deputy.model';

@Injectable({
  providedIn: 'root',
})
export class DeputyService {

  private apiDeputyService: ApiDeputyService = inject(ApiDeputyService);

  private errorService: ErrorService = inject(ErrorService);

  private userAclService: UserAclService = inject(UserAclService);

  private readonly deputiesState = signal<IDeputy[]>([]);

  private readonly loadingState = signal<boolean>(false);

  private loaded: boolean = false;

  /** The colleagues the current user can stand in for (loaded once per session). */
  public readonly deputies: Signal<IDeputy[]> = this.deputiesState.asReadonly();

  /** Whether the deputies request is currently in flight. */
  public readonly loading: Signal<boolean> = this.loadingState.asReadonly();

  /**
   * Method: Load the deputies once per session, only for broker companies / broker employees.
   * Sobrado admins and insurers are skipped — the backend returns nothing for them anyway.
   * Idempotent, so it is safe to call both from the dashboard page (preload) and the user menu
   * (fallback) without firing a duplicate request.
   */
  public async ensureDeputiesLoaded(): Promise<void> {
    if (this.loaded || this.loadingState()) {
      return;
    }
    if (!this.userAclService.isBrokerCompanyOrEmployee() || this.userAclService.isApplicationAdmin()) {
      return;
    }
    this.loadingState.set(true);
    try {
      const deputies = await this.loadMyDeputies();
      this.deputiesState.set(deputies);
      this.loaded = true;
    } finally {
      this.loadingState.set(false);
    }
  }

  /**
   * Method: Load the colleagues the current user is a deputy of.
   * @returns A promise that resolves to the list of deputies (empty on error).
   */
  public async loadMyDeputies(): Promise<IDeputy[]> {
    try {
      const result = await lastValueFrom(this.apiDeputyService.getMyDeputiesObservable());
      const deputies = (result.data?.myDeputies ?? []) as IDeputy[];
      this.errorService.showResponseErrors(result);
      return deputies;
    } catch (err) {
      this.errorService.showGraphQLErrors(err);
      return [];
    }
  }

}
